<script setup>
import { watch, computed, ref } from "vue"
import { useQuery } from "@vue/apollo-composable"
import { MegaphoneIcon } from "@heroicons/vue/24/outline"
import ME_QUERY from "@/graphql/User/MeQuery.gql"
import REFERRAL_REVENUE_QUERY from "@/graphql/Report/ReferralRevenueQuery.gql"
import XTable from "@/components/common/XTable.vue"
import ReferralProgramContent from "@/components/common/ReferralProgramContent.vue"
import Tabs from "@/components/common/Tabs.vue"
import { DateTime } from "luxon"
import { formatCurrency } from "@/utils/formatCurrency"

const { result: user, loading, error: userError } = useQuery(ME_QUERY)

const me = computed(() => user.value?.me || {})

// Tab configuration
const activeTab = ref(0)
const tabs = [
  { 
    labelDesktop: '🔗 Your Referral Link',
    labelMobile: '🔗 Your Link',
    ariaLabel: 'Your Referral Link'
  },
  { 
    labelDesktop: 'ℹ️ Referral Program Overview',
    labelMobile: 'ℹ️ Overview',
    ariaLabel: 'Referral Program Overview'
  }
]


const referralCode = computed(
  () =>
    "https://app.ytz.com/register/?ref=" +
    (me.value?.id || ""),
)

const yesterdaysDate = computed(() => {
  return DateTime.now().minus({ days: 1 }).toFormat("MMMM d, yyyy")
})

const reportVariables = computed(() => ({
  filters: {
    dateRange: [
      DateTime.local()
        .minus({ years: 1 })
        .startOf("month")
        .toFormat("yyyy-LL-dd"),
      DateTime.local().endOf("month").toFormat("yyyy-LL-dd"),
    ],
  },
  fields: ["month", "referralPayoutFrom", "referralPayout"],
  sortOrder: tableSortByValue.value,
}))

let tableSortByValue = ref([
  {
    field: "month",
    sortField: "month",
    direction: "desc",
  },
])

const {
  result: reportResult,
  loading: reportLoading,
  error: reportError,
} = useQuery(REFERRAL_REVENUE_QUERY, reportVariables)

// Transform referralPayoutFrom into a readable format
const formatReferralSource = referralPayoutFrom => {
  // results should be in a string format like 1234 - allIn_email@gmail.com
  // we want to strip the "{id} - allIn_" from the start of the string
  // we want to return the rest of the email after allIn_

  const email = referralPayoutFrom.split(" - ")[1]

  return email
}

// Update subAffiliateCommissions to use the formatted ID
const subAffiliateCommissions = computed(() => {
  return (
    reportResult.value?.report?.data.map(item => ({
      id: formatReferralSource(item.referralPayoutFrom),
      month: item.month,
      commission: item.referralPayout,
    })) || []
  )
})

</script>

<template>
  <div class="max-w-screen-lg text-sm pb-4">
    <div class="">
      <!-- <div
        class="flex relative bg-sky-200 dark:bg-transparent border-sky-300 dark:border-sky-400  dark:text-sky-400 border py-6 px-10 flex gap-3 text-sky-700 rounded-lg"
      >
        <div>
          <div class="flex flex-row align-center gap-2 items-center">
            <MegaphoneIcon
              class="hidden md:block h-8 w-8 -ml-4 mr-2 -rotate-[16deg] self-center text-sky-700/20 block"
            />
            <p class="leading-snug font-semibold">
              Refer Sub-Affiliates to receive <span class="font-semibold">10%</span> of their commissions on the first 6 months.
            </p>
          </div>
        </div>
      </div> -->

      <SectionHeader>
        <template #left>
          <h2 class="text-base font-semibold text-base-content">YTZ Referral Program</h2>
        </template>
      </SectionHeader>
      
<Tabs :tabs="tabs" v-model:activeTab="activeTab">
  <template #tab-0>
    <div class="flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-base-content">Refer Sub-Affiliates to receive 10% of their commissions on the first 6 months.</h3>
        <div class="flex items-center gap-2 text-xs text-base-content/70 hidden sm:block">
          <!-- <v-icon name="md-share-round" scale="0.9" /> -->
          <span>Share to earn 10% commission</span>
        </div>
      </div>
      
      <div class="bg-base-100 rounded-lg border border-base-300 p-4">
        <div class="flex items-center gap-3">
          <div class="flex-1">
            <div class="text-xs text-base-content/70 mb-1">Referral URL</div>
            <div class="font-mono text-xs text-base-content break-all">
              {{ referralCode }}
            </div>
          </div>
          <div class="flex-shrink-0">
            <CopyToClipboard 
              :value="referralCode"
              class="btn btn-primary text-white dark:text-primary-content btn-sm flex gap-1"
              @copied="() => {}"
            >
              <v-icon name="md-contentcopy-round" scale="0.8"/>
              <span class="hidden sm:inline">Copy</span>
            </CopyToClipboard>
          </div>
        </div>
      </div>
      
      <div class="flex flex-wrap gap-2">
        <div class="badge text-white dark:text-primary-content py-2.5 px-3 badge-primary badge-sm rounded-md">
          <v-icon name="md-monetizationon-round" scale="0.7" class="mr-2 -ml-2" />
          10% commission for 6 months
        </div>
        <div class="badge text-white dark:text-primary-content py-2.5 px-3 badge-primary badge-sm rounded-md">
          <v-icon name="md-people-round" scale="0.7" class="mr-2 -ml-2" />
          Unlimited referrals
        </div>
      </div>
    </div>
  </template>
  
  <template #tab-1>
    <ReferralProgramContent />
  </template>
</Tabs>
    </div>

    <!-- New Table Section -->
    <div class="table-container mt-12">
      <!-- <h2 class="font-semibold mb-2 text-base">Sub-Affiliate Commissions</h2>
      <p class="text-base-content/70 mb-4 text-xs">
        *Updated at {{ yesterdaysDate }}
      </p> -->
      <SectionHeader>
        <template #left>
          <h2 class="text-base font-semibold text-base-content">Sub-Affiliate Commissions</h2>
        </template>
        <template #right>
          <p class="text-base-content/70 text-xs">
          *Updated at {{ yesterdaysDate }}
        </p>
        </template>
      </SectionHeader>
      <XTable
        :data="subAffiliateCommissions"
        :is-loading="reportLoading"
        emptyText="No sub-affiliate commission data available."
        :fillHeight="false"
      >
        <template v-slot:header-row>
          <th>Referred Affiliate ID</th>
          <th>Month</th>
          <th>Commission</th>
        </template>
        <template v-slot:row="{ row }">
          <td>{{ row.id }}</td>
          <td>{{ row.month }}</td>
          <td>{{ formatCurrency(row.commission) }}</td>
        </template>
      </XTable>
    </div>
  </div>
</template>

<style scoped>
/* Table container styles */
.table-container {
  @apply mt-8;
}


</style>
